<script lang="ts">
	import { onMount } from 'svelte';
	import {
		Card, Table, TableHead, TableHeadCell, TableBody, TableBodyRow, TableBodyCell,
		Button, Modal, Label, Input, Select, Toggle, Spinner, Badge, Textarea
	} from 'flowbite-svelte';
	import PageHeader from '../../components/PageHeader.svelte';
	import RowAction from '../../components/admin/RowAction.svelte';
	import ScalingNodesView from '../../components/admin/ScalingNodesView.svelte';
	import {
		listScalingGroups, createScalingGroup, updateScalingGroup, deleteScalingGroup,
		scaleUpGroup, scaleDownGroup, listProbes, getProbe, getScalingStatus, testScalingGroup
	} from '../../AppNetwork';
	import type { ScalingGroup, Probe, ProbeMetric } from '../../AppTypes';

	let groups: ScalingGroup[] = [];
	let probes: Probe[] = [];
	let loading = true;
	let error = '';
	let message = '';
	let expandedId: number | null = null;
	let nodesRefreshKey = 0;
	let reconcilerEnabled = true; // assume on until told otherwise (avoid banner flash)

	let showModal = false;
	let editing: ScalingGroup | null = null;
	let form = blankForm();
	let isSaving = false;
	let modalError = '';

	const COMPARISONS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'];
	const BUILTIN_METRIC_OPTIONS = [
		{ key: '$up', label: 'Availability ($up)' },
		{ key: '$responseTime', label: 'Response time ($responseTime)' }
	];

	// Same pattern as the services page: per-probe metric definitions fetched
	// lazily and offered alongside the built-ins.
	let probeMetricsMap: Record<number, ProbeMetric[]> = {};

	// `map` is passed in (not read from scope) so the template expression
	// re-evaluates when the lazy metric fetch updates probeMetricsMap.
	function metricKeyOptions(probeId: number | null, map: Record<number, ProbeMetric[]>): { key: string; label: string }[] {
		const provided = probeId !== null ? (map[probeId] ?? []).map((m) => ({ key: m.key, label: `${m.label} (${m.key})` })) : [];
		const options = [...BUILTIN_METRIC_OPTIONS, ...provided];
		// Keep a stale/unknown stored key selectable instead of silently clearing it.
		if (form.metricKey && !options.some((o) => o.key === form.metricKey)) {
			options.push({ key: form.metricKey, label: form.metricKey });
		}
		return options;
	}

	async function ensureProbeMetrics(probeId: number | null) {
		if (probeId === null || probeMetricsMap[probeId]) return;
		try {
			const full = await getProbe(probeId);
			probeMetricsMap = { ...probeMetricsMap, [probeId]: full.metrics ?? [] };
		} catch {
			probeMetricsMap = { ...probeMetricsMap, [probeId]: [] };
		}
	}

	function blankForm() {
		return {
			name: '',
			enabled: false,
			probeId: null as number | null,
			metricKey: '',
			scaleUpComparison: 'gt',
			scaleUpValue: '',
			scaleDownComparison: 'lt',
			scaleDownValue: '',
			evalWindow: 3,
			minNodes: 0,
			maxNodes: 3,
			cooldownSeconds: 300,
			serverType: 'cx22',
			location: 'nbg1',
			imageId: '',
			hetznerSshKeyName: '',
			networkId: '',
			loadBalancerId: '',
			lbUsePrivateIp: false,
			swarmJoinAddress: '',
			nodeLabelsText: '',
			managerSshHost: '',
			managerSshPort: 22,
			managerSshUser: 'root',
			hetznerToken: '',
			joinToken: '',
			sshPrivateKey: ''
		};
	}

	async function load() {
		loading = true;
		error = '';
		try {
			groups = (await listScalingGroups({ limit: 100 })).items;
		} catch (e) {
			error = e instanceof Error ? e.message : 'Failed to load scaling groups';
		}
		loading = false;
	}

	onMount(async () => {
		await load();
		try {
			probes = (await listProbes({ limit: 100 })).items;
		} catch {
			// probe picker degrades to a free id — non-fatal
		}
		try {
			reconcilerEnabled = (await getScalingStatus()).reconcilerEnabled;
		} catch {
			// status check is advisory only
		}
	});

	function toggleExpand(id: number) {
		expandedId = expandedId === id ? null : id;
		nodesRefreshKey++;
	}

	function openCreate() {
		editing = null;
		form = blankForm();
		modalError = '';
		showModal = true;
	}

	function openDuplicate(group: ScalingGroup) {
		openEdit(group);
		editing = null; // create a new group from these values
		form.name = `${group.name} (copy)`;
		// Secrets are write-only and can't be copied — the copy can't provision
		// until they're re-entered, so it starts disabled.
		form.enabled = false;
	}

	function openEdit(group: ScalingGroup) {
		editing = group;
		modalError = '';
		void ensureProbeMetrics(group.probeId);
		form = {
			...blankForm(),
			name: group.name,
			enabled: group.enabled,
			probeId: group.probeId,
			metricKey: group.metricKey ?? '',
			scaleUpComparison: group.scaleUpComparison ?? 'gt',
			scaleUpValue: group.scaleUpValue ?? '',
			scaleDownComparison: group.scaleDownComparison ?? 'lt',
			scaleDownValue: group.scaleDownValue ?? '',
			evalWindow: group.evalWindow,
			minNodes: group.minNodes,
			maxNodes: group.maxNodes,
			cooldownSeconds: group.cooldownSeconds,
			serverType: group.serverType,
			location: group.location,
			imageId: group.imageId,
			hetznerSshKeyName: group.hetznerSshKeyName ?? '',
			networkId: group.networkId ?? '',
			loadBalancerId: group.loadBalancerId ?? '',
			lbUsePrivateIp: group.lbUsePrivateIp,
			swarmJoinAddress: group.swarmJoinAddress,
			nodeLabelsText: Object.entries(group.nodeLabels ?? {})
				.map(([k, v]) => `${k}=${v}`)
				.join('\n'),
			managerSshHost: group.managerSshHost,
			managerSshPort: group.managerSshPort,
			managerSshUser: group.managerSshUser,
			hetznerToken: '',
			joinToken: '',
			sshPrivateKey: ''
		};
		showModal = true;
	}

	function parseLabels(text: string): Record<string, string> | null {
		const entries = text
			.split('\n')
			.map((line) => line.trim())
			.filter(Boolean)
			.map((line) => {
				const idx = line.indexOf('=');
				return idx > 0 ? [line.slice(0, idx).trim(), line.slice(idx + 1).trim()] : null;
			})
			.filter((e): e is [string, string] => !!e);
		return entries.length > 0 ? Object.fromEntries(entries) : null;
	}

	async function save() {
		isSaving = true;
		modalError = '';
		const payload: Record<string, unknown> = {
			name: form.name,
			enabled: form.enabled,
			probeId: form.probeId,
			metricKey: form.metricKey || null,
			scaleUpComparison: form.scaleUpComparison,
			scaleUpValue: form.scaleUpValue || null,
			scaleDownComparison: form.scaleDownComparison,
			scaleDownValue: form.scaleDownValue || null,
			evalWindow: form.evalWindow,
			minNodes: form.minNodes,
			maxNodes: form.maxNodes,
			cooldownSeconds: form.cooldownSeconds,
			serverType: form.serverType,
			location: form.location,
			imageId: form.imageId,
			hetznerSshKeyName: form.hetznerSshKeyName || null,
			networkId: form.networkId || null,
			loadBalancerId: form.loadBalancerId || null,
			lbUsePrivateIp: form.lbUsePrivateIp,
			swarmJoinAddress: form.swarmJoinAddress,
			nodeLabels: parseLabels(form.nodeLabelsText),
			managerSshHost: form.managerSshHost,
			managerSshPort: form.managerSshPort,
			managerSshUser: form.managerSshUser
		};
		// Secrets are write-only: only send when the admin typed a new value.
		if (form.hetznerToken) payload.hetznerToken = form.hetznerToken;
		if (form.joinToken) payload.joinToken = form.joinToken;
		if (form.sshPrivateKey) payload.sshPrivateKey = form.sshPrivateKey;

		try {
			if (editing) await updateScalingGroup(editing.id, payload);
			else await createScalingGroup(payload);
			showModal = false;
			await load();
		} catch (e) {
			modalError = e instanceof Error ? e.message : 'Failed to save group';
		}
		isSaving = false;
	}

	async function remove(group: ScalingGroup) {
		if (!confirm(`Delete scaling group "${group.name}"? It must have no nodes.`)) return;
		try {
			await deleteScalingGroup(group.id);
			await load();
		} catch (e) {
			message = e instanceof Error ? e.message : 'Failed to delete group';
		}
	}

	async function scaleUp(group: ScalingGroup) {
		message = '';
		try {
			await scaleUpGroup(group.id);
			message = `Provisioning a new node for ${group.name}…`;
			expandedId = group.id;
			nodesRefreshKey++;
		} catch (e) {
			message = e instanceof Error ? e.message : 'Failed to scale up';
		}
	}

	async function scaleDown(group: ScalingGroup) {
		message = '';
		try {
			await scaleDownGroup(group.id);
			message = `Draining the newest node of ${group.name}…`;
			expandedId = group.id;
			nodesRefreshKey++;
		} catch (e) {
			message = e instanceof Error ? e.message : 'Failed to scale down';
		}
	}

	async function testConnection(group: ScalingGroup) {
		message = `Testing ${group.name}…`;
		try {
			const result = await testScalingGroup(group.id);
			const parts = [
				result.hetzner.ok ? 'Hetzner API: OK' : `Hetzner API: ${result.hetzner.error}`,
				result.managerSsh.ok ? 'Manager SSH: OK' : `Manager SSH: ${result.managerSsh.error}`
			];
			message = `${group.name} — ${parts.join(' · ')}`;
		} catch (e) {
			message = e instanceof Error ? e.message : 'Connection test failed';
		}
	}

	function probeName(probeId: number | null): string {
		if (probeId === null) return '—';
		return probes.find((p) => p.id === probeId)?.name ?? `#${probeId}`;
	}
</script>

<PageHeader
	title="Auto-Scaling"
	description="Scaling groups provision Hetzner Cloud servers, join them to the Docker Swarm and register them on the load balancer based on probe metrics."
/>

<div class="space-y-6">
	{#if !reconcilerEnabled}
		<div class="rounded-lg border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
			<i class="fa-solid fa-triangle-exclamation me-2"></i>
			<strong>The auto-scaling reconcile loop is not running on this server.</strong>
			Nodes will stay in <em>Provisioning</em> and no automatic scaling will happen. Set
			<code class="font-mono">ABSTRACT_STATUS_AUTOSCALING_ENABLED=true</code> (with the scheduler enabled) and restart the server.
		</div>
	{/if}
	<Card class="max-w-none">
		<div class="flex items-center justify-between gap-4">
			<p class="text-sm text-gray-500 dark:text-gray-400">
				{#if message}<i class="fa-solid fa-circle-info me-2"></i>{message}{/if}
			</p>
			<Button on:click={openCreate}>
				<i class="fa-solid fa-plus me-2"></i>New Scaling Group
			</Button>
		</div>
	</Card>

	<Card padding="none" class="max-w-none">
		{#if loading}
			<div class="p-8 text-center">
				<i class="fa-solid fa-spinner fa-spin text-2xl text-gray-400 mb-2"></i>
				<p class="text-gray-500 dark:text-gray-400">Loading scaling groups...</p>
			</div>
		{:else if error}
			<div class="p-8 text-center">
				<i class="fa-solid fa-exclamation-triangle text-4xl text-red-500 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">{error}</p>
				<Button class="mt-4" on:click={load}>Retry</Button>
			</div>
		{:else if groups.length === 0}
			<div class="p-8 text-center">
				<i class="fa-solid fa-server text-4xl text-gray-300 dark:text-gray-600 mb-4"></i>
				<p class="text-gray-500 dark:text-gray-400">No scaling groups configured</p>
			</div>
		{:else}
			<Table striped divClass="relative overflow-x-auto rounded-lg">
				<TableHead class="bg-gray-50 dark:bg-gray-700">
					<TableHeadCell class="w-8"></TableHeadCell>
					<TableHeadCell>Name</TableHeadCell>
					<TableHeadCell>Trigger</TableHeadCell>
					<TableHeadCell>Capacity</TableHeadCell>
					<TableHeadCell>Server</TableHeadCell>
					<TableHeadCell>Enabled</TableHeadCell>
					<TableHeadCell>Actions</TableHeadCell>
				</TableHead>
				<TableBody>
					{#each groups as group (group.id)}
						<TableBodyRow class="group cursor-pointer" on:click={() => toggleExpand(group.id)}>
							<TableBodyCell class="text-gray-400">
								<i class="fa-solid fa-chevron-right transition-transform {expandedId === group.id ? 'rotate-90' : ''}"></i>
							</TableBodyCell>
							<TableBodyCell class="font-medium text-gray-900 dark:text-white">{group.name}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400 text-xs">
								{#if group.metricKey}
									<span class="font-mono">{probeName(group.probeId)} / {group.metricKey}</span>
									<span class="block">
										up {group.scaleUpComparison} {group.scaleUpValue} · down {group.scaleDownComparison} {group.scaleDownValue} · last {group.evalWindow}
									</span>
								{:else}
									manual only
								{/if}
							</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400">{group.minNodes}–{group.maxNodes}</TableBodyCell>
							<TableBodyCell class="text-gray-500 dark:text-gray-400 text-xs font-mono">{group.serverType} @ {group.location}</TableBodyCell>
							<TableBodyCell>
								{#if group.enabled}<Badge color="green">On</Badge>{:else}<Badge color="dark">Off</Badge>{/if}
							</TableBodyCell>
							<TableBodyCell on:click={(e) => e.stopPropagation()}>
								<div class="flex gap-1 opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity">
									<RowAction icon="fa-plug" label="Test connection (Hetzner + manager SSH)" on:click={() => testConnection(group)} />
									<RowAction icon="fa-plus" label="Scale up now" on:click={() => scaleUp(group)} />
									<RowAction icon="fa-minus" label="Scale down now" on:click={() => scaleDown(group)} />
									<RowAction icon="fa-pen" label="Edit" on:click={() => openEdit(group)} />
									<RowAction icon="fa-copy" label="Duplicate" on:click={() => openDuplicate(group)} />
									<RowAction icon="fa-trash" label="Delete" danger on:click={() => remove(group)} />
								</div>
							</TableBodyCell>
						</TableBodyRow>
						{#if expandedId === group.id}
							<TableBodyRow>
								<TableBodyCell colspan={7} class="p-4 bg-gray-50 dark:bg-gray-800/50">
									{#key nodesRefreshKey}
										<ScalingNodesView groupId={group.id} />
									{/key}
								</TableBodyCell>
							</TableBodyRow>
						{/if}
					{/each}
				</TableBody>
			</Table>
		{/if}
	</Card>
</div>

<Modal bind:open={showModal} title={editing ? 'Edit Scaling Group' : 'New Scaling Group'} size="lg">
	<div class="space-y-5">
		<div class="grid grid-cols-2 gap-4">
			<div><Label class="mb-1">Name</Label><Input bind:value={form.name} /></div>
			<div class="flex items-end pb-1"><Toggle bind:checked={form.enabled}>Enabled (automatic scaling)</Toggle></div>
		</div>

		<div>
			<h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Trigger (probe metric)</h3>
			<div class="grid grid-cols-2 gap-4">
				<div>
					<Label class="mb-1">Probe</Label>
					<Select
						bind:value={form.probeId}
						on:change={() => {
							void ensureProbeMetrics(form.probeId);
							if (form.probeId !== null && !form.metricKey) form.metricKey = '$up';
						}}
					>
						<option value={null}>— manual only —</option>
						{#each probes as probe}
							<option value={probe.id}>{probe.name}</option>
						{/each}
					</Select>
				</div>
				<div>
					<Label class="mb-1">Metric</Label>
					<Select bind:value={form.metricKey} disabled={form.probeId === null}>
						{#each metricKeyOptions(form.probeId, probeMetricsMap) as opt}<option value={opt.key}>{opt.label}</option>{/each}
					</Select>
				</div>
				<div class="grid grid-cols-2 gap-2">
					<div>
						<Label class="mb-1">Scale up when</Label>
						<Select bind:value={form.scaleUpComparison}>
							{#each COMPARISONS as c}<option value={c}>{c}</option>{/each}
						</Select>
					</div>
					<div><Label class="mb-1">Value</Label><Input bind:value={form.scaleUpValue} placeholder="80" /></div>
				</div>
				<div class="grid grid-cols-2 gap-2">
					<div>
						<Label class="mb-1">Scale down when</Label>
						<Select bind:value={form.scaleDownComparison}>
							{#each COMPARISONS as c}<option value={c}>{c}</option>{/each}
						</Select>
					</div>
					<div><Label class="mb-1">Value</Label><Input bind:value={form.scaleDownValue} placeholder="20" /></div>
				</div>
				<div>
					<Label class="mb-1">Sample window (all N must agree)</Label>
					<Input type="number" bind:value={form.evalWindow} min="1" />
				</div>
				<div>
					<Label class="mb-1">Cooldown (seconds)</Label>
					<Input type="number" bind:value={form.cooldownSeconds} min="0" />
				</div>
			</div>
		</div>

		<div>
			<h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Capacity</h3>
			<div class="grid grid-cols-2 gap-4">
				<div><Label class="mb-1">Min nodes</Label><Input type="number" bind:value={form.minNodes} min="0" /></div>
				<div><Label class="mb-1">Max nodes</Label><Input type="number" bind:value={form.maxNodes} min="1" /></div>
			</div>
		</div>

		<div>
			<h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Hetzner Cloud</h3>
			<div class="grid grid-cols-2 gap-4">
				<div><Label class="mb-1">Server type</Label><Input bind:value={form.serverType} placeholder="cx22" /></div>
				<div><Label class="mb-1">Location</Label><Input bind:value={form.location} placeholder="nbg1" /></div>
				<div>
					<Label class="mb-1">Base image (snapshot id)</Label>
					<Input bind:value={form.imageId} placeholder="123456789" />
					<p class="text-xs text-gray-400 mt-1">Must have Docker preinstalled.</p>
				</div>
				<div>
					<Label class="mb-1">SSH key name (optional)</Label>
					<Input bind:value={form.hetznerSshKeyName} placeholder="Hetzner SSH key resource" />
				</div>
				<div><Label class="mb-1">Private network id (optional)</Label><Input bind:value={form.networkId} /></div>
				<div><Label class="mb-1">Load balancer id (optional)</Label><Input bind:value={form.loadBalancerId} /></div>
				<div class="col-span-2"><Toggle bind:checked={form.lbUsePrivateIp}>Register LB target via private IP</Toggle></div>
				<div>
					<Label class="mb-1">API token {editing?.hasHetznerToken ? '(set — leave blank to keep)' : ''}</Label>
					<Input type="password" bind:value={form.hetznerToken} autocomplete="off" />
				</div>
			</div>
		</div>

		<div>
			<h3 class="text-sm font-semibold text-gray-900 dark:text-white mb-2">Docker Swarm</h3>
			<div class="grid grid-cols-2 gap-4">
				<div><Label class="mb-1">Join address</Label><Input bind:value={form.swarmJoinAddress} placeholder="10.0.0.2:2377" /></div>
				<div>
					<Label class="mb-1">Worker join token {editing?.hasJoinToken ? '(set — leave blank to keep)' : ''}</Label>
					<Input type="password" bind:value={form.joinToken} autocomplete="off" placeholder="SWMTKN-1-…" />
				</div>
				<div class="col-span-2">
					<Label class="mb-1">Node labels (one key=value per line, optional)</Label>
					<Textarea bind:value={form.nodeLabelsText} rows={2} placeholder={'worker=true\nzone=auto'} />
				</div>
				<div><Label class="mb-1">Manager SSH host</Label><Input bind:value={form.managerSshHost} /></div>
				<div class="grid grid-cols-2 gap-2">
					<div><Label class="mb-1">Port</Label><Input type="number" bind:value={form.managerSshPort} /></div>
					<div><Label class="mb-1">User</Label><Input bind:value={form.managerSshUser} /></div>
				</div>
				<div class="col-span-2">
					<Label class="mb-1">SSH private key {editing?.hasSshPrivateKey ? '(set — leave blank to keep)' : ''}</Label>
					<Textarea bind:value={form.sshPrivateKey} rows={3} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" />
					<p class="text-xs text-gray-400 mt-1">Used for both the new nodes (as root) and the swarm manager.</p>
				</div>
			</div>
		</div>

		{#if modalError}<p class="text-red-500 text-sm">{modalError}</p>{/if}
	</div>
	<svelte:fragment slot="footer">
		<div class="flex gap-2">
			<Button color="primary" on:click={save} disabled={isSaving || !form.name}>
				{#if isSaving}<Spinner size="sm" class="mr-2" />{/if}Save
			</Button>
			<Button color="alternative" on:click={() => (showModal = false)}>Cancel</Button>
		</div>
	</svelte:fragment>
</Modal>
